import {
  Control,
  FieldArrayWithId,
  FieldErrors,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form'

import { Button, Icon, Switch } from '@clientbase/clientbase-library'
import { Box, Stack, Typography } from '@mui/material'
import { v4 } from 'uuid'

import { formatMoney } from 'utils'
import { priceConverter } from 'utils/FormatMoney'

import { theme } from 'styles/theme'

import { NewExpenseSchema } from '../../schema'
import AllocationRow from '../AllocationRow'
import { distributeEqually } from './distribute'

type AllocationsSectionProps = {
  control: Control<NewExpenseSchema>
  watch: UseFormWatch<NewExpenseSchema>
  setValue: UseFormSetValue<NewExpenseSchema>
  errors: FieldErrors<NewExpenseSchema>
  allocationsFields: FieldArrayWithId<NewExpenseSchema, 'allocations', 'id'>[]
  appendAllocation: UseFieldArrayAppend<NewExpenseSchema, 'allocations'>
  removeAllocation: UseFieldArrayRemove
  totalValue: number
  onToggleRateio: (next: boolean) => void
}

const AllocationsSection = ({
  control,
  watch,
  setValue,
  errors,
  allocationsFields,
  appendAllocation,
  removeAllocation,
  totalValue,
  onToggleRateio
}: AllocationsSectionProps) => {
  const enableRateio = !!watch('enableRateio')

  const totalAllocated = (watch('allocations') ?? []).reduce(
    (acc, allocation) =>
      acc + priceConverter((allocation?.amount ?? 0) as unknown as number),
    0
  )
  const remaining = totalValue - totalAllocated
  const remainingPercentage =
    totalValue > 0 ? Number(((remaining / totalValue) * 100).toFixed(2)) : 0

  const handleDistributeEqually = () => {
    distributeEqually(totalValue, allocationsFields.length).forEach(
      ({ amount, percentage }, index) => {
        setValue(`allocations.${index}.amount`, amount, {
          shouldValidate: true
        })
        setValue(`allocations.${index}.percentage`, percentage)
      }
    )
  }

  return (
    <Stack gap="16px" my={2} mt={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="mdBold">Categoria e centro de custo</Typography>

        <Stack direction="row" alignItems="center" gap={2}>
          <Box sx={{ '& .MuiTypography-root': { fontWeight: 400 } }}>
            <Switch
              label="Habilitar rateio"
              checked={enableRateio}
              onChange={(event) => onToggleRateio(event.target.checked)}
            />
          </Box>
          {enableRateio && allocationsFields.length > 1 && (
            <Button
              variantButton="ghost"
              disabled={totalValue <= 0}
              onClick={handleDistributeEqually}
              sx={{ width: 'fit-content' }}
            >
              <Stack direction="row" gap="5px" alignItems="center">
                <Icon
                  icon="compareArrows"
                  sx={{ color: theme.palette.neutral[400], fontSize: '18px' }}
                />
                <Typography
                  sx={{
                    color: theme.palette.neutral[400],
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Distribuir igualmente
                </Typography>
              </Stack>
            </Button>
          )}
          <Button
            variantButton="ghost"
            disabled={!enableRateio}
            onClick={() =>
              appendAllocation({
                uuid: v4(),
                category: { label: '', value: '' },
                costCenter: { label: '', value: '' },
                amount: undefined,
                percentage: undefined
              })
            }
            sx={{ width: 'fit-content' }}
          >
            <Stack direction="row" gap="5px" alignItems="center">
              <Icon
                icon="add"
                sx={{ color: theme.palette.neutral[400], fontSize: '18px' }}
              />
              <Typography
                sx={{
                  color: theme.palette.neutral[400],
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Adicionar rateio
              </Typography>
            </Stack>
          </Button>
        </Stack>
      </Stack>

      <Stack gap="8px">
        {allocationsFields.map((field, index) => (
          <AllocationRow
            key={field.uuid || field.id}
            index={index}
            control={control}
            watch={watch}
            setValue={setValue}
            errors={errors}
            totalValue={totalValue}
            enableRateio={enableRateio}
            removeAllocation={removeAllocation}
            canRemove={enableRateio && allocationsFields.length > 1}
          />
        ))}
      </Stack>

      <Box sx={{ height: '1px', bgcolor: theme.palette.neutral[700] }} />

      {enableRateio && (
        <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
          <Typography variant="smBold" color={theme.palette.neutral[400]}>
            Total rateado: {formatMoney(totalAllocated)}
          </Typography>
          <Typography
            variant="smBold"
            color={
              Math.abs(remaining) >= 0.01
                ? theme.palette.error[200]
                : theme.palette.neutral[400]
            }
          >
            {`Restante do rateio: ${formatMoney(
              remaining
            )} | ${remainingPercentage}%`}
          </Typography>
        </Stack>
      )}
    </Stack>
  )
}

export default AllocationsSection
